import * as React from 'react'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import { addDays } from 'date-fns'
import type { CalendarEvent } from '../../typings'
import { compareEventsByStart } from '../../eventOrder'
import { overlapsRange } from '../../eventSpan'
import { dayHeading, eventCountLabel } from '../../dateFormat'
import { DayColumn } from './DayColumn'
import { UpNextRail } from './UpNextRail'
import { TimeScroller } from './TimeScroller'

type Props = {
  /** The day to render. */
  cursor: Date
  events: CalendarEvent[]
}

/** Day view — a positioned day column beside the "Up next" rail. */
export function DayGrid({ cursor, events }: Props) {
  const theme = useTheme()

  const { dayEvents, allDayEvents, timedEvents } = React.useMemo(() => {
    const all = events
      .filter((event) => overlapsRange(event, cursor, addDays(cursor, 1)))
      .sort(compareEventsByStart)
    return {
      dayEvents: all,
      allDayEvents: all.filter((event) => event.allDay),
      timedEvents: all.filter((event) => !event.allDay),
    }
  }, [events, cursor])

  return (
    <Box>
      <Box sx={{ mb: '12px' }}>
        <Box
          sx={{
            fontFamily: theme.typography.fontFamily,
            fontSize: '16px',
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {dayHeading(cursor)}
        </Box>
        <Box
          sx={{
            fontFamily: theme.typography.fontFamily,
            fontSize: '13px',
            fontWeight: 400,
            color: theme.palette.text.secondary,
          }}
        >
          {eventCountLabel(dayEvents.length)}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: '20px',
          alignItems: 'stretch',
        }}
      >
        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
          <TimeScroller days={[cursor]} label="Day view">
            <DayColumn
              day={cursor}
              allDayEvents={allDayEvents}
              timedEvents={timedEvents}
            />
          </TimeScroller>
        </Box>
        <Box sx={{ flex: { xs: '1 1 100%', lg: '0 0 280px' } }}>
          <UpNextRail events={dayEvents} />
        </Box>
      </Box>
    </Box>
  )
}
